import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../api/client';
import { contactsApi, type Campaign, type CampaignField, type Contact } from '../api/campaigns';
import { Alert, Button, Card, Section, StatusChip } from '../components';
import { useI18n, type TranslationKey } from '../i18n';
import { useSession } from '../auth/session-context';
import { PREVIEW_MODE, PREVIEW_TELEPHONY_HINT } from '../preview/preview-mode';
import { dialerApi, telephonyApi, type CallSummary, type CallerStatus } from './telephony-api';
import { useRealtime } from './useRealtime';
import { WrapUpForm, formatDuration } from './CallPanel';

/**
 * Der Arbeitsplatz eines Callers.
 *
 * Eine Fläche, ein großer Knopf, ein klarer Zustand. Die Zustandsnamen des
 * Servers erscheinen bewusst nirgends - der Caller liest "Bereit",
 * "Es klingelt", "Im Gespräch", nicht AVAILABLE oder RINGING
 * (02_navigation_ux_tutorial_i18n.md).
 *
 * Der Zustand kommt vom Server: über Realtime-Ereignisse und zusätzlich
 * regelmäßig abgefragt, damit die Anzeige auch nach einem kurzen
 * Verbindungsabriss wieder stimmt. Der Browser entscheidet nichts selbst.
 */

/** Wie oft der eigene Zustand nachgefragt wird. */
const ABFRAGE_MS = 2_000;

export function DialerPanel({
  campaign,
  fields,
  onFinished,
}: {
  campaign: Campaign;
  fields: CampaignField[];
  onFinished?: () => void;
}): JSX.Element {
  const { t, tError } = useI18n();
  const { handleApiError } = useSession();

  const [status, setStatus] = useState<CallerStatus>('ONLINE');
  const [call, setCall] = useState<CallSummary | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const laufend = useRef(false);

  const pool = campaign.campaignMode === 'pool';

  /** Holt den eigenen Zustand beim Server ab - er ist die maßgebliche Quelle. */
  const abgleichen = useCallback(async () => {
    if (laufend.current) {
      return;
    }
    laufend.current = true;
    try {
      const antwort = (await dialerApi.me()) as {
        status: CallerStatus;
        campaignId: string | null;
        call?: CallSummary | null;
      };
      setStatus(antwort.campaignId === campaign.id ? antwort.status : 'ONLINE');
      setCall(antwort.call ?? null);
    } catch (caught) {
      handleApiError(caught);
    } finally {
      laufend.current = false;
    }
  }, [campaign.id, handleApiError]);

  useEffect(() => {
    void abgleichen();
    const timer = window.setInterval(() => void abgleichen(), ABFRAGE_MS);
    return () => window.clearInterval(timer);
  }, [abgleichen]);

  // Realtime beschleunigt die Anzeige; richtig wird sie durch den Abgleich.
  useRealtime((event) => {
    if (
      event.event.startsWith('caller.') ||
      event.event.startsWith('call.') ||
      event.event.startsWith('dialer.')
    ) {
      void abgleichen();
    }
  });

  // Datensatz zum laufenden Gespräch nachladen.
  useEffect(() => {
    if (!call?.contactId) {
      setContact(null);
      return;
    }
    void contactsApi
      .get(campaign.id, call.contactId)
      .then((antwort) => setContact(antwort.contact))
      .catch(() => setContact(null));
  }, [call?.contactId, campaign.id]);

  // Gesprächsdauer aus dem serverseitigen Verbindungszeitpunkt.
  useEffect(() => {
    if (!call?.connectedAt || call.status === 'ended') {
      setSeconds(0);
      return;
    }
    const start = new Date(call.connectedAt).getTime();
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [call?.connectedAt, call?.status]);

  const run = (aktion: () => Promise<unknown>): void => {
    void (async () => {
      setError(null);
      setBusy(true);
      try {
        await aktion();
        await abgleichen();
      } catch (caught) {
        handleApiError(caught);
        setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      } finally {
        setBusy(false);
      }
    })();
  };

  const bereit = status !== 'ONLINE' && status !== 'OFFLINE';
  const imGespraech = status === 'IN_CALL' || status === 'RECONNECTING';
  const nachbearbeitung = status === 'WRAP_UP';

  return (
    <Card>
      <Section
        title={pool ? t('dialer.poolTitle') : t('dialer.assignedTitle')}
        description={pool ? t('dialer.poolHint') : t('dialer.assignedHint')}
        actions={<StatusChip tone={tonZu(status)}>{t(statusText(status))}</StatusChip>}
      >
        {PREVIEW_MODE ? <Alert tone="info">{PREVIEW_TELEPHONY_HINT}</Alert> : null}
        {error ? <Alert tone="error">{error}</Alert> : null}
        {!campaign.isActive ? <Alert tone="info">{t('error.campaign.stopped')}</Alert> : null}

        {imGespraech && contact ? (
          <div className="card">
            <dl className="facts">
              <dt>{t('contact.phone')}</dt>
              <dd>
                <strong>{contact.phone}</strong>
              </dd>
              <dt>{t('contact.firstName')}</dt>
              <dd>{contact.firstName ?? '–'}</dd>
              <dt>{t('contact.lastName')}</dt>
              <dd>{contact.lastName ?? '–'}</dd>
              {fields.map((field) => (
                <FeldZeile key={field.id} label={field.fieldName} value={contact.fields[field.id]} />
              ))}
            </dl>
          </div>
        ) : null}

        <p className="call-zustand">
          <span>{t(statusText(status))}</span>
          {imGespraech ? <span className="call-dauer">{formatDuration(seconds)}</span> : null}
        </p>

        <div className="row-actions">
          {!bereit ? (
            <Button
              variant="primary"
              size="gross"
              disabled={busy || !campaign.isActive}
              onClick={() => run(() => dialerApi.ready(campaign.id))}
            >
              {pool ? t('dialer.acceptCalls') : t('dialer.start')}
            </Button>
          ) : null}

          {bereit && !imGespraech && !nachbearbeitung ? (
            <>
              {status === 'PAUSED' ? (
                <Button
                  variant="primary"
                  size="gross"
                  disabled={busy}
                  onClick={() => run(() => dialerApi.ready(campaign.id))}
                >
                  {t('dialer.resume')}
                </Button>
              ) : (
                <Button variant="secondary" disabled={busy} onClick={() => run(() => dialerApi.pause(campaign.id))}>
                  {t('dialer.pause')}
                </Button>
              )}
              <Button variant="ghost" disabled={busy} onClick={() => run(() => dialerApi.stop(campaign.id))}>
                {pool ? t('dialer.stopAccepting') : t('dialer.stop')}
              </Button>
            </>
          ) : null}

          {imGespraech && call ? (
            <>
              <Button
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    const naechster = !muted;
                    setMuted(naechster);
                    await telephonyApi.mute(call.id, naechster);
                  })
                }
              >
                {muted ? t('call.unmute') : t('call.mute')}
              </Button>
              <Button variant="danger" disabled={busy} onClick={() => run(() => telephonyApi.hangup(call.id))}>
                {t('call.hangup')}
              </Button>
            </>
          ) : null}
        </div>

        {nachbearbeitung && call ? (
          <WrapUpForm
            call={call}
            fields={fields}
            onDone={() => {
              setCall(null);
              setContact(null);
              setMuted(false);
              onFinished?.();
              void abgleichen();
            }}
          />
        ) : null}
      </Section>
    </Card>
  );
}

function FeldZeile({ label, value }: { label: string; value: string | undefined }): JSX.Element | null {
  if (!value) {
    return null;
  }
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/** Der Zustand in Alltagssprache - nie der technische Name. */
function statusText(status: CallerStatus): TranslationKey {
  return ('dialer.state.' + status) as TranslationKey;
}

function tonZu(status: CallerStatus): 'neutral' | 'erfolg' | 'info' | 'warnung' {
  if (status === 'IN_CALL') {
    return 'erfolg';
  }
  if (status === 'AVAILABLE') {
    return 'info';
  }
  if (status === 'PAUSED' || status === 'WRAP_UP' || status === 'RECONNECTING') {
    return 'warnung';
  }
  return 'neutral';
}
